import * as React from 'react'

import {
  useSocialUploadsCreateSignedUrlMutation,
  socialGetUploadedQueryFetcher,
} from '@web/gen/feed'

export interface UploadedFile extends File {
  uploadedUrl: string
  previewUrl?: string
}

export async function fetchUploadedImage(assetId: string) {
  const response = await socialGetUploadedQueryFetcher({
    input: { assetId },
  })()

  if (response.socialGetUploaded?.errors?.length) {
    // oxlint-disable-next-line promise/avoid-new
    await new Promise(resolve => setTimeout(resolve, 2000))
    return fetchUploadedImage(assetId)
  }
  return response.socialGetUploaded
}

export function useUploadsImages() {
  const socialUploadsCreateSignedUrlMutation = useSocialUploadsCreateSignedUrlMutation()
  const [loading, setLoading] = React.useState(false)

  const uploadImage = async (file: File) => {
    try {
      const signedUrlResult = await socialUploadsCreateSignedUrlMutation.mutateAsync({
        input: {
          fileName: file.name,
          fileSize: file.size,
        },
      })

      const { id, signedUrl, inputFormFields } = signedUrlResult.socialCreateSignedUploadUrlV2

      if (!inputFormFields || !Array.isArray(inputFormFields)) {
        throw new Error('Missing form fields for S3 upload')
      }

      const formData = new FormData()

      inputFormFields.forEach(({ key, value }) => {
        formData.append(key, value)
      })

      const hasContentType = inputFormFields.some(f => f.key === 'Content-Type')
      if (!hasContentType) {
        formData.append('Content-Type', file.type)
      }

      formData.append('file', file)
      await fetch(signedUrl, {
        method: 'POST',
        body: formData,
      })

      const uploadedImage = await fetchUploadedImage(id)
      return uploadedImage.imageUrl as string
    } catch (error) {
      throw new Error('Failed to upload image: ' + (error as Error).message)
    }
  }

  const uploadImages = async (files: File[]): Promise<UploadedFile[]> => {
    setLoading(true)
    try {
      const uploadPromises = files.map(async file => {
        const castFile = file as UploadedFile
        castFile.uploadedUrl = await uploadImage(file)
        castFile.previewUrl = URL.createObjectURL(file)
        return castFile
      })
      // oxlint-disable-next-line unicorn/no-await-expression-member
      return (await Promise.allSettled(uploadPromises))
        .filter(result => result.status === 'fulfilled')
        .map(result => (result as PromiseFulfilledResult<UploadedFile>).value) as UploadedFile[]
    } finally {
      setLoading(false)
    }
  }

  return { uploadImages, uploadImage, loading }
}
